import { WebIO } from "@gltf-transform/core";
import { dedup, prune } from "@gltf-transform/functions";

const MAX_TEXTURE_SIZE = 2048;
const JPEG_QUALITY = 0.85;

/**
 * Optimize a GLB file in the browser:
 * 1. Deduplicate accessors / textures
 * 2. Prune unused nodes
 * 3. Resize any texture dimension exceeding MAX_TEXTURE_SIZE
 * 4. Re-encode all textures as JPEG via <canvas>
 *
 * Returns an optimized GLB as a File ready for upload.
 */
export async function optimizeGlb(
  file: File,
  onProgress?: (step: string) => void
): Promise<File> {
  onProgress?.("Reading model...");
  const arrayBuffer = await file.arrayBuffer();

  const io = new WebIO();
  const doc = await io.readBinary(new Uint8Array(arrayBuffer));

  onProgress?.("Deduplicating...");
  await doc.transform(dedup());

  onProgress?.("Pruning unused data...");
  await doc.transform(prune());

  // Resize + compress textures to WebP via canvas (browser-compatible,
  // no sharp dependency)
  onProgress?.("Compressing textures...");
  const root = doc.getRoot();
  const textures = root.listTextures();

  for (let i = 0; i < textures.length; i++) {
    const texture = textures[i];
    const imageData = texture.getImage();
    if (!imageData || imageData.byteLength === 0) continue;

    onProgress?.(`Compressing texture ${i + 1}/${textures.length}...`);

    try {
      const jpegData = await resizeAndConvertToJpeg(
        imageData,
        texture.getMimeType(),
        MAX_TEXTURE_SIZE
      );
      texture.setImage(jpegData);
      texture.setMimeType("image/jpeg");
    } catch {
      // If conversion fails for a texture, keep the original
    }
  }

  onProgress?.("Writing optimized model...");
  const optimizedData = await io.writeBinary(doc);

  const sizeBefore = (file.size / 1024 / 1024).toFixed(1);
  const sizeAfter = (optimizedData.byteLength / 1024 / 1024).toFixed(1);
  onProgress?.(`Done — ${sizeBefore} MB → ${sizeAfter} MB`);

  return new File([optimizedData], file.name, {
    type: "model/gltf-binary",
  });
}

/**
 * Resize an image (if needed) and convert to JPEG using an offscreen canvas.
 * JPEG is used instead of WebP for maximum compatibility with model-viewer
 * across all browser and device configurations.
 * Dimensions are capped at maxSize on each axis while preserving aspect ratio.
 */
async function resizeAndConvertToJpeg(
  imageData: Uint8Array,
  mimeType: string,
  maxSize: number
): Promise<Uint8Array> {
  const blob = new Blob([imageData], { type: mimeType });
  const bitmap = await createImageBitmap(blob);

  let { width, height } = bitmap;

  // Scale down if either dimension exceeds the max
  if (width > maxSize || height > maxSize) {
    const scale = maxSize / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");

  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const jpegBlob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      "image/jpeg",
      JPEG_QUALITY
    );
  });

  return new Uint8Array(await jpegBlob.arrayBuffer());
}
